import React, { useEffect, useRef, useState } from "react";
import {
  Bike,
  Footprints,
  Heart,
  PersonStanding,
  RefreshCw,
  Sailboat,
  StepForward,
  StretchHorizontal,
  Zap,
} from "lucide-react";
import { useConnectedEquipment } from "../../contexts/ConnectedEquipmentContext";
import { EQUIPMENT } from "../../utils/ftms";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useToast } from "../../contexts/ToastContext";
import { DeleteConfirm, Field, ItemRow, SwitchRow } from "../profile/ProfileParts";
import { isAirBike, setAirBike } from "../../utils/airBike";
import { cn } from "../../lib/utils";

const ICONS = {
  Bike,
  Sailboat,
  Footprints,
  PersonStanding,
  StepForward,
  StretchHorizontal,
};

export const equipmentIcon = (type) =>
  type === "hr_monitor" ? Heart : ICONS[EQUIPMENT[type]?.icon] || Bike;

const STATUS = {
  connected: { label: "Connected", dot: "bg-green-500" },
  reconnecting: { label: "Reconnecting", dot: "bg-yellow-500" },
  connecting: { label: "Connecting", dot: "bg-blue-500" },
  requesting: { label: "Connecting", dot: "bg-blue-500" },
  error: { label: "Couldn't connect", dot: "bg-red-500" },
};
const statusOf = (status) => STATUS[status] || { label: "Not connected", dot: "bg-muted-foreground/60" };

// Concept2 PM5 (and a few mid-range trainers) silently sleep when idle and
// drop the GATT link. Web Bluetooth on Android Chrome can stall reconnect
// attempts in that case — surface what the user needs to physically do
// rather than letting them assume the app froze.
const wakeHint = (type) => {
  if (type === "hr_monitor") return "Touch the strap to wake it.";
  if (type === "rower") return "Pull the handle to wake your PM5.";
  if (type === "indoor_bike") return "Pedal a couple of strokes to wake the trainer.";
  return "Waiting for the device to advertise again…";
};

const formatRowerLine = (m) => {
  const spm = m?.stroke_rate_spm != null ? `${Math.round(m.stroke_rate_spm)} spm` : "— spm";
  if (m?.pace_sec_per_500m != null) {
    const sec = m.pace_sec_per_500m;
    const min = Math.floor(sec / 60);
    const rem = String(Math.round(sec % 60)).padStart(2, "0");
    return `${spm} · ${min}:${rem} /500 m`;
  }
  return `${spm} · — /500 m`;
};

const formatBikeLine = (m) => {
  const rpm = m?.cadence_rpm != null ? `${Math.round(m.cadence_rpm)} rpm` : "— rpm";
  const kmh = m?.speed_kmh != null ? `${m.speed_kmh.toFixed(1)} km/h` : "— km/h";
  return `${rpm} · ${kmh}`;
};

const useLiveMetrics = (subscribe) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    if (!subscribe) return undefined;
    const unsub = subscribe((snap) => setM(snap));
    return () => unsub();
  }, [subscribe]);
  return m;
};

/** What a paired item is showing right now, as one line. */
export const useLiveLine = (item) => {
  const live = useLiveMetrics(item?.subscribe);
  if (!item || item.status !== "connected") return null;
  if (item.type === "hr_monitor") {
    const bpm = live?.bpm ?? item.snapshot?.bpm ?? null;
    return `${bpm != null ? Math.round(bpm) : "—"} bpm`;
  }
  const watts = live?.power_watts != null ? `${Math.round(live.power_watts)} W` : "— W";
  return `${watts} · ${item.type === "rower" ? formatRowerLine(live) : formatBikeLine(live)}`;
};

/**
 * Everything the Bluetooth group lists, in one shape: paired machines, the
 * heart-rate strap, and ones remembered but not reachable right now (out of
 * range here, or saved to the account from another phone).
 */
export const useBluetoothItems = () => {
  const eq = useConnectedEquipment();
  const items = [
    ...eq.devices.map((d) => ({ ...d, key: `ftms:${d.id}`, kind: "ftms", active: d.id === eq.activeDeviceId })),
    ...(eq.hr ? [{ ...eq.hr, key: "hr", kind: "hr", type: "hr_monitor" }] : []),
    ...eq.rememberedDevices.map((d) => ({ ...d, key: `saved:${d.id}`, kind: "saved", status: "saved" })),
    ...(eq.rememberedHr
      ? [{ ...eq.rememberedHr, key: "saved-hr", kind: "saved", type: "hr_monitor", status: "saved" }]
      : []),
  ];
  return { eq, items };
};

/**
 * Chrome on Android forgets Bluetooth permissions when it closes unless two
 * flags are on, so remembered gear can't come back on its own (#169). Pages
 * can't open chrome:// links, hence copying the address.
 */
export const ReconnectFlagsTip = () => {
  const { showToast } = useToast();
  const copy = () => {
    navigator.clipboard
      ?.writeText("chrome://flags")
      .then(() => showToast({ title: "Copied. Paste it into Chrome's address bar." }))
      .catch(() => {});
  };
  return (
    <div className="grid gap-1.5 rounded-xl border border-border bg-background p-3 text-xs leading-snug text-muted-foreground">
      <p className="font-semibold text-foreground">Chrome forgets your gear when it closes</p>
      <p>To have it reconnect by itself:</p>
      <ol className="list-decimal space-y-0.5 pl-4">
        <li>
          Open{" "}
          <button type="button" onClick={copy} className="font-mono text-foreground underline underline-offset-2">
            chrome://flags
          </button>{" "}
          in Chrome (tap to copy).
        </li>
        <li>
          Turn on <span className="text-foreground">Experimental Web Platform features</span> and{" "}
          <span className="text-foreground">Web Bluetooth new permissions backend</span>.
        </li>
        <li>Relaunch Chrome, then reconnect once.</li>
      </ol>
      <p>Until then, tap Reconnect and pick it in the list.</p>
    </div>
  );
};

const savedDetail = (item) =>
  item.foreign
    ? "Saved to your account · pair it here"
    : item.needsPicker
      ? "Saved · tap to reconnect"
      : "Saved · not in range";

/** A row on the Settings page for one paired or remembered item. */
export const BluetoothRow = ({ item, onClick }) => {
  const line = useLiveLine(item);
  const status = statusOf(item.status);
  const detail =
    item.kind === "saved" ? (
      <>
        <span className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-muted-foreground/60 align-[1px]" />
        {savedDetail(item)}
      </>
    ) : (
      <>
        <span className={cn("mr-1.5 inline-block h-[7px] w-[7px] rounded-full align-[1px]", status.dot)} />
        {line || status.label}
      </>
    );
  return (
    <ItemRow
      Icon={equipmentIcon(item.type)}
      title={item.name || "Saved device"}
      detail={detail}
      tag={
        item.active ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
            In use
          </span>
        ) : null
      }
      onClick={onClick}
    />
  );
};

/**
 * One paired item's sheet: its live reading, why it's not connected and what
 * to do about it, which machine feeds the workout, and Forget (asked first).
 */
const BluetoothSheet = ({ itemKey, onClose }) => {
  const { eq, items } = useBluetoothItems();
  const { showToast } = useToast();
  const current = items.find((i) => i.key === itemKey) || null;

  // Keep the last item's content while the sheet animates closed.
  const [shown, setShown] = useState(current);
  useEffect(() => {
    if (current) setShown(current);
  }, [current]);
  const item = current || shown;

  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [itemKey]);

  // Air bike (Echo, Assault): no VO2max from its rides. Spotted by name.
  const [airBike, setAirBikeState] = useState(false);
  useEffect(() => {
    const it = items.find((i) => i.key === itemKey);
    if (it) setAirBikeState(isAirBike(it));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey]);

  // The user's name for it ("C2 Rower"); saved when the sheet closes.
  const [name, setName] = useState("");
  useEffect(() => {
    if (itemKey == null) return;
    const it = items.find((i) => i.key === itemKey);
    setName(it && it.name !== it.advertisedName ? it.name : "");
    // Only when a sheet opens: a reading arriving must not reset the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey]);
  const pending = useRef({});
  pending.current = { name, item: current };
  const close = () => {
    const { name: draft, item: it } = pending.current;
    if (it) {
      const was = it.name !== it.advertisedName ? it.name : "";
      if (draft.trim() !== was) {
        eq.setEquipmentLabel(it, draft.trim()).catch((err) =>
          showToast({ title: "Couldn't save the name to your account", description: err.message }),
        );
      }
    }
    onClose();
  };

  const line = useLiveLine(current);
  if (!item) return null;

  const status = statusOf(item.status);
  const isHr = item.type === "hr_monitor";
  const saved = item.kind === "saved";
  const connected = item.status === "connected";
  const canReconnect = !saved && item.status !== "connected" && item.status !== "connecting";
  const pairing = isHr ? eq.hrPairing : eq.pairing;
  const pairError = isHr ? eq.hrPairError : eq.pairError;

  const forget = () => {
    if (isHr) eq.forgetHrMonitor();
    else eq.forgetDevice(item.id);
    onClose();
  };
  const advertised = item.advertisedName || item.name;

  return (
    <DetailSheet open={!!current} onOpenChange={(o) => !o && close()} title={item.name || "Saved device"}>
      <div className="grid gap-2 rounded-xl border border-border bg-background p-3">
        <div className="flex items-center gap-2 text-sm">
          <span className={cn("h-2 w-2 rounded-full", saved ? "bg-muted-foreground/60" : status.dot)} />
          <span className="font-semibold">{saved ? savedDetail(item) : status.label}</span>
          {item.active && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
              In use
            </span>
          )}
        </div>
        {connected && line && (
          <div className="flex items-center gap-1.5 font-mono text-sm tabular-nums">
            {!isHr && <Zap className="h-3.5 w-3.5 text-yellow-400" />}
            {isHr && <Heart className="h-3.5 w-3.5 text-red-500" />}
            {line}
          </div>
        )}
        {(item.status === "reconnecting" || saved) && (
          <p className="text-xs leading-snug text-muted-foreground">{wakeHint(item.type)}</p>
        )}
        {item.error && item.status !== "reconnecting" && (
          <p className="text-xs text-destructive">{item.error}</p>
        )}
      </div>

      <Field
        label="Name"
        htmlFor="gear-name"
        hint={`Shown instead of "${advertised}". Leave it empty to use that name.`}
      >
        <Input
          id="gear-name"
          value={name}
          maxLength={60}
          placeholder={advertised}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      {item.type === "indoor_bike" && (
        <SwitchRow
          id="air-bike"
          label="Air bike"
          description="Arms and legs, with a fan, like a Rogue Echo or Assault bike. Its rides don't count towards your VO2max, which needs a legs-only bike with measured power."
          checked={airBike}
          onCheckedChange={(v) => {
            setAirBike(item.id, v);
            setAirBikeState(v);
          }}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {saved && (
          <Button
            type="button"
            onClick={() => (isHr ? eq.addHrMonitor : eq.addDevice)({ name: advertised })}
            disabled={pairing}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            {item.foreign ? "Pair here" : "Reconnect"}
          </Button>
        )}
        {canReconnect && (
          <Button type="button" variant="outline" onClick={eq.reconnectPersisted}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Reconnect
          </Button>
        )}
        {!saved && item.status === "error" && (
          <Button
            type="button"
            variant="outline"
            onClick={() => (isHr ? eq.addHrMonitor : eq.addDevice)({ name: advertised })}
            disabled={pairing}
          >
            Pair again
          </Button>
        )}
        {!isHr && connected && !item.active && (
          <Button type="button" variant="outline" onClick={() => eq.setActive(item.id)}>
            Use for workouts
          </Button>
        )}
      </div>
      {saved && pairError && <p className="text-xs text-destructive">{pairError}</p>}
      {saved && item.needsPicker && !item.foreign && <ReconnectFlagsTip />}

      <p className="text-xs leading-snug text-muted-foreground">
        {isHr
          ? "Stays connected for the whole session, including floor work between machines."
          : "The machine in use feeds power, cadence and pace into your workout. With two paired, choose which one."}
      </p>

      {!saved && (
        <DeleteConfirm
          action="Forget"
          name={item.name}
          consequence="You'll need to pair it again to use it."
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={forget}
        />
      )}
    </DetailSheet>
  );
};

export default BluetoothSheet;
